import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Heart, House, PiggyBank, Plus, Scales, Tag } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '../ui/Button';
import { CategoryChip, CategoryChipDragHandle } from './CategoryChip';
import { paths } from '../../routes/paths';
import { cn } from '../../utils/cn';
import {
  ALLOCATION_BUCKET_IDS,
  groupCategoriesIntoBuckets,
  planBucketMove,
} from '../../utils/categoryAllocation';
import pt from '../../locales/pt';

const BUCKET_META = {
  essential: {
    icon: House,
    titleKey: 'essential',
    subtitleKey: 'essentialSubtitle',
    shell: 'border-sage-200/80 bg-sage-50/80 dark:border-sage-800/60 dark:bg-sage-950/40',
    iconShell: 'bg-sage-100 text-sage-800 dark:bg-sage-900/60 dark:text-sage-200',
  },
  lifestyle: {
    icon: Heart,
    titleKey: 'lifestyle',
    subtitleKey: 'lifestyleSubtitle',
    shell: 'border-amber-200/80 bg-amber-50/70 dark:border-amber-900/50 dark:bg-amber-950/30',
    iconShell: 'bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200',
  },
  personal_investment: {
    icon: PiggyBank,
    titleKey: 'savings',
    subtitleKey: 'savingsSubtitle',
    shell: 'border-sky-200/80 bg-sky-50/70 dark:border-sky-900/50 dark:bg-sky-950/30',
    iconShell: 'bg-sky-100 text-sky-900 dark:bg-sky-950/50 dark:text-sky-200',
  },
};

function bucketsToIdMap(buckets) {
  return {
    orphan: buckets.orphan.map((c) => c.id),
    essential: buckets.essential.map((c) => c.id),
    lifestyle: buckets.lifestyle.map((c) => c.id),
    personal_investment: buckets.personal_investment.map((c) => c.id),
  };
}

function SortableCategoryChip({ category, bucketId, isOrphan, onEdit, onArchive }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: category.id, data: { bucketId } });

  return (
    <CategoryChip
      category={category}
      bucketId={bucketId}
      isOrphan={isOrphan}
      setNodeRef={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.45 : 1,
      }}
      dragHandle={
        <CategoryChipDragHandle attributes={attributes} listeners={listeners} />
      }
      onEdit={onEdit}
      onArchive={onArchive}
    />
  );
}

function BucketDropZone({ bucketId, children, className }) {
  const { setNodeRef, isOver } = useDroppable({ id: bucketId, data: { bucketId } });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        className,
        isOver && 'ring-2 ring-sage-400/50 ring-offset-1 ring-offset-background',
      )}
    >
      {children}
    </div>
  );
}

function AllocationBucket({ bucketId, categories, onEdit, onArchive, onAdd }) {
  const meta = BUCKET_META[bucketId];
  const Icon = meta.icon;
  const title = pt.categories.buckets[meta.titleKey];
  const subtitle = pt.categories.buckets[meta.subtitleKey];

  return (
    <BucketDropZone
      bucketId={bucketId}
      className={cn('rounded-[18px] border p-space-3', meta.shell)}
    >
      <div className="mb-space-3 flex items-center gap-space-2-5">
        <span
          className={cn(
            'flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-ds-sm',
            meta.iconShell,
          )}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <h2 className="font-display min-w-0 flex-1 text-type-title-card font-bold leading-tight text-foreground">
          {title}
        </h2>
        <span className="type-chip shrink-0 text-right text-muted-foreground">
          {subtitle} · {categories.length}
        </span>
      </div>
      <SortableContext items={categories.map((c) => c.id)} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-2 gap-space-2 xl:grid-cols-1">
          {categories.map((category) => (
            <SortableCategoryChip
              key={category.id}
              category={category}
              bucketId={bucketId}
              onEdit={onEdit}
              onArchive={onArchive}
            />
          ))}
          <button
            type="button"
            onClick={() => onAdd(bucketId)}
            className="flex min-h-[44px] items-center justify-center gap-space-2 rounded-ds-md border border-dashed border-border/90 px-space-2 py-space-2 type-label text-muted-foreground transition-colors hover:border-border hover:bg-card/60 hover:text-foreground"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            {pt.categories.buckets.addInBucket}
          </button>
        </div>
      </SortableContext>
    </BucketDropZone>
  );
}

export function CategoryBucketsBoard({
  categories,
  orphans,
  onReorder,
  onReclassify,
  onEdit,
  onArchive,
  onAdd,
  onClassifyClick,
}) {
  const grouped = useMemo(
    () => groupCategoriesIntoBuckets(categories, orphans),
    [categories, orphans],
  );
  const byId = useMemo(() => {
    const map = new Map();
    for (const c of categories) map.set(c.id, c);
    return map;
  }, [categories]);

  const [items, setItems] = useState(() => bucketsToIdMap(grouped));
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    setItems(bucketsToIdMap(grouped));
  }, [grouped]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const categoriesFor = (bucketId) =>
    (items[bucketId] || []).map((id) => byId.get(id)).filter(Boolean);

  const findBucketOf = (id) =>
    ['orphan', ...ALLOCATION_BUCKET_IDS].find((key) =>
      (items[key] || []).includes(id),
    ) || null;

  const resolveOverBucket = (over) => {
    if (!over) return null;
    if (over.data?.current?.bucketId) return over.data.current.bucketId;
    const id = String(over.id);
    if (items[id]) return id;
    return findBucketOf(id);
  };

  const handleDragStart = (event) => setActiveId(event.active.id);

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;
    const overBucket = resolveOverBucket(over);
    const activeBucket = active.data?.current?.bucketId || findBucketOf(active.id);
    if (!overBucket || !activeBucket || overBucket === activeBucket) return;
    if (overBucket === 'orphan') return;

    const planned = planBucketMove(items, {
      activeId: active.id,
      overId: String(over.id),
      overBucketId: overBucket,
    });
    if (planned) setItems(planned.items);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) {
      setItems(bucketsToIdMap(grouped));
      return;
    }

    const originalMap = bucketsToIdMap(grouped);
    const originalBucket =
      ['orphan', ...ALLOCATION_BUCKET_IDS].find((key) =>
        (originalMap[key] || []).includes(active.id),
      ) || null;

    const sameBucketPlan = planBucketMove(items, {
      activeId: active.id,
      overId: String(over.id),
      overBucketId: resolveOverBucket(over),
    });
    const nextItems = sameBucketPlan?.items || items;
    if (sameBucketPlan) setItems(nextItems);

    const currentBucket =
      ['orphan', ...ALLOCATION_BUCKET_IDS].find((key) =>
        (nextItems[key] || []).includes(active.id),
      ) || null;

    const orderedIds = ['orphan', ...ALLOCATION_BUCKET_IDS].flatMap(
      (key) => nextItems[key] || [],
    );

    const allocationChanged =
      Boolean(originalBucket) &&
      Boolean(currentBucket) &&
      originalBucket !== currentBucket &&
      currentBucket !== 'orphan';

    try {
      if (allocationChanged) {
        await onReclassify(active.id, currentBucket);
        toast.success(pt.categories.reclassifiedToast);
      }
      await onReorder(orderedIds);
    } catch (err) {
      setItems(bucketsToIdMap(grouped));
      toast.error(typeof err === 'string' ? err : pt.categories.reclassifyError);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setItems(bucketsToIdMap(grouped));
  };

  const orphanCats = categoriesFor('orphan');
  const activeCategory = activeId ? byId.get(activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex flex-col gap-space-4">
        {/* Scope banner */}
        <div className="flex items-start gap-space-2-5 rounded-ds-md border border-sage-200/80 bg-sage-50/90 p-space-3 dark:border-sage-800/50 dark:bg-sage-950/40">
          <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-ds-sm bg-sage-100 text-sage-800 dark:bg-sage-900/60 dark:text-sage-200">
            <Tag className="h-3.5 w-3.5" aria-hidden />
          </span>
          <p className="type-caption leading-relaxed text-muted-foreground">
            {pt.categories.scopeBanner.beforeLink}{' '}
            <Link
              to={paths.budget}
              className="font-semibold text-sage-800 underline-offset-2 hover:underline dark:text-sage-200"
            >
              {pt.categories.scopeBanner.link}
            </Link>
            {pt.categories.scopeBanner.afterLink}
          </p>
        </div>

        {/* Orphan banner */}
        {orphanCats.length > 0 ? (
          <div className="flex flex-col gap-space-2">
            <div className="flex items-center gap-space-2-5 rounded-ds-md border border-warning-500/40 bg-warning-50/90 p-space-3 dark:border-warning-700/40 dark:bg-warning-950/35">
              <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-ds-sm bg-white/40 text-warning-800 dark:bg-white/5 dark:text-warning-200">
                <Scales className="h-4 w-4" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <p className="type-label text-warning-800 dark:text-warning-200">
                  {pt.categories.orphans.title.replace('{n}', orphanCats.length)}
                </p>
                <p className="type-caption mt-0.5 leading-snug text-muted-foreground">
                  {pt.categories.orphans.subtitle}
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                className="h-8 shrink-0 rounded-ds-sm bg-warning-700 px-space-3 type-label text-white hover:bg-warning-800 dark:bg-warning-600"
                onClick={() => onClassifyClick(orphanCats[0])}
              >
                {pt.categories.orphans.classify}
              </Button>
            </div>

            <BucketDropZone bucketId="orphan" className="rounded-ds-md">
              <SortableContext
                items={orphanCats.map((c) => c.id)}
                strategy={rectSortingStrategy}
              >
                <div
                  className={cn(
                    'grid gap-space-2',
                    orphanCats.length === 1 ? 'grid-cols-1' : 'grid-cols-2',
                  )}
                >
                  {orphanCats.map((category) => (
                    <SortableCategoryChip
                      key={category.id}
                      category={category}
                      bucketId="orphan"
                      isOrphan
                      onEdit={onEdit}
                      onArchive={onArchive}
                    />
                  ))}
                </div>
              </SortableContext>
            </BucketDropZone>
          </div>
        ) : null}

        {/* Allocation buckets */}
        <div className="grid grid-cols-1 gap-space-3 md:grid-cols-2 md:gap-space-4 xl:grid-cols-3">
          {ALLOCATION_BUCKET_IDS.map((bucketId) => (
            <AllocationBucket
              key={bucketId}
              bucketId={bucketId}
              categories={categoriesFor(bucketId)}
              onEdit={onEdit}
              onArchive={onArchive}
              onAdd={onAdd}
            />
          ))}
        </div>
      </div>

      <DragOverlay>
        {activeCategory ? (
          <CategoryChip
            category={activeCategory}
            bucketId="essential"
            className="shadow-lg"
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
